/**
 * NPSModal — Net Promoter Score survey shown after 10 min cumulative app usage.
 * 3 steps: score (0-10) → followup question → thank you.
 * Repeatable every 30 days. Gated by feature flag "nps_survey".
 */
import { useState, useEffect, useCallback } from 'react'
import { Modal, View, Text, TextInput, Pressable, StyleSheet, Platform } from 'react-native'
import { KeyboardAvoidingView } from 'react-native-keyboard-controller'
import { Heart, Check, ArrowRight, X } from 'lucide-react-native'
import { useAuth } from '../auth/AuthProvider'
import { useFeatureFlags } from '../context/FeatureFlagsContext'
import { apiClient } from '../api/client'
import { events } from '../lib/posthog'
import { haptics } from '../utils/haptics'
import { persistentStorage } from '../lib/storageAdapter'
import { colors, spacing, borderRadius, typography, shadows } from '../theme'

const NPS_USAGE_THRESHOLD_S = 600 // 10 minutes

function shouldShowNPS(user: { last_nps_at?: string | null; onboarding_completed?: boolean }, usageSeconds: number): boolean {
  if (!user.onboarding_completed) return false
  if (usageSeconds < NPS_USAGE_THRESHOLD_S) return false
  if (!user.last_nps_at) return true
  const daysSinceNPS = (Date.now() - new Date(user.last_nps_at).getTime()) / 86_400_000
  return daysSinceNPS >= 30
}

function scoreToCategory(score: number): 'detractor' | 'passive' | 'promoter' {
  if (score <= 6) return 'detractor'
  if (score <= 8) return 'passive'
  return 'promoter'
}

function getFollowupQuestion(score: number): string {
  if (score <= 6) return 'Hvad skal vi ændre for at du ville anbefale os?'
  if (score <= 8) return 'Hvad mangler der for at give os 9 eller 10?'
  return 'Hvad er det bedste ved appen?'
}

export default function NPSModal() {
  const { user, isAuthenticated, refreshUser } = useAuth()
  const { isEnabled } = useFeatureFlags()
  const [dismissed, setDismissed] = useState(false)
  const [step, setStep] = useState<'score' | 'followup' | 'done'>('score')
  const [score, setScore] = useState<number | null>(null)
  const [followup, setFollowup] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [aiResponse, setAiResponse] = useState<string | null>(null)
  const [usageSeconds, setUsageSeconds] = useState(0)

  // Poll cumulative usage time (written by _layout.tsx tracker)
  useEffect(() => {
    persistentStorage.load('app_usage_seconds').then(() => {
      setUsageSeconds(parseInt(persistentStorage.get('app_usage_seconds') || '0', 10))
    })
    const interval = setInterval(() => {
      setUsageSeconds(parseInt(persistentStorage.get('app_usage_seconds') || '0', 10))
    }, 60_000)
    return () => clearInterval(interval)
  }, [])

  const visible =
    !dismissed &&
    isAuthenticated &&
    isEnabled('nps_survey') &&
    user != null &&
    shouldShowNPS(user, usageSeconds)

  const handleSelectScore = useCallback((s: number) => {
    haptics.selection()
    setScore(s)
  }, [])

  const handleNext = useCallback(() => {
    if (score === null) return
    haptics.medium()
    setStep('followup')
  }, [score])

  const handleSubmit = useCallback(async () => {
    if (score === null || submitting) return
    setSubmitting(true)
    haptics.medium()
    const category = scoreToCategory(score)
    try {
      const res = await apiClient.nps.submit({
        score,
        followup_answer: followup.trim() || null,
      })
      events.npsSubmitted(score, category)
      if (res.data?.ai_response) setAiResponse(res.data.ai_response)
    } catch {
      // Show done anyway
    }
    setSubmitting(false)
    setStep('done')
  }, [score, followup, submitting])

  const handleSkipFollowup = useCallback(async () => {
    if (score === null || submitting) return
    setSubmitting(true)
    haptics.light()
    const category = scoreToCategory(score)
    try {
      const res = await apiClient.nps.submit({ score, followup_answer: null })
      events.npsSubmitted(score, category)
      if (res.data?.ai_response) setAiResponse(res.data.ai_response)
    } catch {
      // Show done anyway
    }
    setSubmitting(false)
    setStep('done')
  }, [score, submitting])

  const handleDismiss = useCallback(async () => {
    events.npsDismissed()
    setDismissed(true)
    await refreshUser?.()
  }, [refreshUser])

  const handleDone = useCallback(async () => {
    setDismissed(true)
    await refreshUser?.()
  }, [refreshUser])

  if (!visible) return null

  return (
    <Modal visible transparent animationType="fade" statusBarTranslucent>
      <KeyboardAvoidingView style={{ flex: 1 }} behavior={Platform.OS === 'ios' ? 'padding' : undefined}>
      <View style={s.overlay}>
        <View style={s.backdrop} />
        <View style={s.modal}>
          {/* Close button */}
          {step !== 'done' && (
            <Pressable style={s.closeBtn} onPress={handleDismiss}>
              <X size={18} color={colors.text.muted} />
            </Pressable>
          )}

          <View style={s.iconWrap}>
            {step === 'done' ? (
              <Check size={32} color={colors.success} />
            ) : (
              <Heart size={32} color={colors.primary} />
            )}
          </View>

          {step === 'score' && (
            <>
              <Text style={s.title}>
                Hvor sandsynligt er det at du{'\n'}ville anbefale appen til en ven?
              </Text>

              <View style={s.scoreGrid}>
                <View style={s.scoreRow}>
                  {[0, 1, 2, 3, 4, 5].map((i) => (
                    <Pressable
                      key={i}
                      style={[s.scoreBtn, score === i && s.scoreBtnActive]}
                      onPress={() => handleSelectScore(i)}
                    >
                      <Text style={[s.scoreText, score === i && s.scoreTextActive]}>{i}</Text>
                    </Pressable>
                  ))}
                </View>
                <View style={s.scoreRow}>
                  {[6, 7, 8, 9, 10].map((i) => (
                    <Pressable
                      key={i}
                      style={[s.scoreBtn, score === i && s.scoreBtnActive]}
                      onPress={() => handleSelectScore(i)}
                    >
                      <Text style={[s.scoreText, score === i && s.scoreTextActive]}>{i}</Text>
                    </Pressable>
                  ))}
                </View>
              </View>

              <View style={s.scaleLabels}>
                <Text style={s.scaleLabel}>Slet ikke</Text>
                <Text style={s.scaleLabel}>Meget sandsynligt</Text>
              </View>

              <Pressable
                style={[s.btnPrimary, (score === null || submitting) && s.btnDisabled]}
                onPress={handleNext}
                disabled={score === null || submitting}
              >
                <Text style={s.btnPrimaryText}>Næste</Text>
                <ArrowRight size={18} color={colors.text.inverse} />
              </Pressable>

              <Pressable onPress={handleDismiss} style={s.skipBtn}>
                <Text style={s.skipText}>Spring over</Text>
              </Pressable>
            </>
          )}

          {step === 'followup' && (
            <>
              <Text style={s.title}>{getFollowupQuestion(score!)}</Text>

              <TextInput
                style={s.textInput}
                value={followup}
                onChangeText={setFollowup}
                placeholder="Fortæl os hvad du synes..."
                placeholderTextColor={colors.text.muted}
                multiline
                maxLength={1000}
                textAlignVertical="top"
              />

              <Pressable
                style={[s.btnPrimary, submitting && s.btnDisabled]}
                onPress={handleSubmit}
                disabled={submitting}
              >
                <Text style={s.btnPrimaryText}>
                  {submitting ? 'Sender...' : 'Send'}
                </Text>
              </Pressable>

              <Pressable onPress={handleSkipFollowup} disabled={submitting} style={s.skipBtn}>
                <Text style={s.skipText}>Spring over</Text>
              </Pressable>
            </>
          )}

          {step === 'done' && (
            <>
              <Text style={s.title}>Tak for din feedback!</Text>
              <Text style={s.body}>
                {aiResponse || 'Din mening hjælper os med at gøre appen bedre for alle.'}
              </Text>

              <Pressable style={s.btnPrimary} onPress={handleDone}>
                <Text style={s.btnPrimaryText}>Luk</Text>
              </Pressable>
            </>
          )}
        </View>
      </View>
      </KeyboardAvoidingView>
    </Modal>
  )
}

const s = StyleSheet.create({
  overlay: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  backdrop: {
    ...StyleSheet.absoluteFillObject,
    backgroundColor: 'rgba(0,0,0,0.5)',
  },
  modal: {
    backgroundColor: colors.background,
    borderRadius: borderRadius.xl,
    width: '88%',
    maxWidth: 400,
    padding: spacing.lg,
    paddingTop: spacing.xl,
    paddingBottom: spacing.lg,
    alignItems: 'center',
    ...shadows.lg,
  },
  closeBtn: {
    position: 'absolute',
    top: spacing.md,
    right: spacing.md,
    width: 32,
    height: 32,
    borderRadius: 16,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: colors.surface,
    zIndex: 1,
  },
  iconWrap: {
    width: 64,
    height: 64,
    borderRadius: 32,
    backgroundColor: colors.primaryLight,
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: spacing.md,
  },
  title: {
    ...typography.h2,
    color: colors.text.primary,
    textAlign: 'center',
    marginBottom: spacing.md,
  },
  body: {
    ...typography.body,
    color: colors.text.secondary,
    textAlign: 'center',
    marginBottom: spacing.lg,
    lineHeight: 22,
  },
  scoreGrid: {
    gap: 8,
    width: '100%',
    alignItems: 'center',
    marginBottom: spacing.xs,
  },
  scoreRow: {
    flexDirection: 'row',
    justifyContent: 'center',
    gap: 8,
  },
  scoreBtn: {
    minWidth: 36,
    minHeight: 44,
    borderRadius: 18,
    borderWidth: 1.5,
    borderColor: colors.border,
    alignItems: 'center',
    justifyContent: 'center',
  },
  scoreBtnActive: {
    backgroundColor: colors.primary,
    borderColor: colors.primary,
  },
  scoreText: {
    ...typography.label,
    color: colors.text.secondary,
    fontSize: 13,
  },
  scoreTextActive: {
    color: colors.text.inverse,
  },
  scaleLabels: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    width: '100%',
    paddingHorizontal: 4,
    marginBottom: spacing.lg,
  },
  scaleLabel: {
    ...typography.caption,
    color: colors.text.muted,
  },
  btnPrimary: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: spacing.sm,
    backgroundColor: colors.primary,
    paddingVertical: 14,
    paddingHorizontal: spacing.lg,
    borderRadius: borderRadius.md,
    width: '100%',
    marginBottom: spacing.sm,
  },
  btnPrimaryText: {
    ...typography.bodyBold,
    color: colors.text.inverse,
  },
  btnDisabled: {
    opacity: 0.5,
  },
  skipBtn: {
    paddingVertical: spacing.sm,
  },
  skipText: {
    ...typography.body,
    color: colors.text.muted,
  },
  textInput: {
    borderWidth: 1,
    borderColor: colors.border,
    borderRadius: borderRadius.md,
    padding: spacing.sm,
    ...typography.body,
    color: colors.text.primary,
    height: 96,
    width: '100%',
    marginBottom: spacing.md,
  },
})
